import { create } from 'zustand'
import { Client as APIClient } from '@botpress/client'
import { config, PAT_KEY } from '../shared'
import cookie from 'js-cookie'
import axios, { AxiosInstance } from 'axios'
import { login, Client as HITLClient, createClient } from '@botpress/hitl-client'
import { Zai } from '@botpress/zai'
import { Cognitive } from '@botpress/cognitive'

const API_BASE_URL = config.apiBaseUrl
const DEFAULT_API_CLIENT_KEY = 'DEFAULT_API_CLIENT'

type getApiClientProps = { botId?: string; workspaceId: string } | { botId?: never; workspaceId?: string }

type ClientsState = {
  APIClients: Record<string, APIClient>
  getAPIClient: (props?: getApiClientProps) => APIClient
  addAPIClient: (key: string, client: APIClient) => void
  StudioClients: Record<string, AxiosInstance>
  getStudioClient: (props: { botId: string; workspaceId: string }) => AxiosInstance
  addStudioClient: (key: string, client: AxiosInstance) => void
  getHitlUserKey: (webhookId: string) => Promise<string>
  HITLClients: Record<string, HITLClient>
  addHITLClient: (key: string, client: HITLClient) => void
  getHITLClient: (props: { userKey: string; webhookId: string }) => HITLClient
}

const useClientsStore = create<ClientsState>()((set, get) => ({
  APIClients: {
    DEFAULT_API_CLIENT_KEY: new APIClient({
      apiUrl: API_BASE_URL,
      token: config.usePat ? config.pat ?? cookie.get(PAT_KEY) : undefined,
    }),
  },
  getAPIClient: (props) => {
    const key = props?.botId ? `${props.workspaceId}-${props.botId}` : props?.workspaceId ?? DEFAULT_API_CLIENT_KEY
    const client = get().APIClients[key]

    if (client) {
      return client
    }

    const newClient = new APIClient({
      apiUrl: API_BASE_URL,
      workspaceId: props?.workspaceId,
      token: config.usePat ? config.pat ?? cookie.get(PAT_KEY) : undefined,
      botId: props?.botId,
    })

    set((state) => ({
      APIClients: { ...state.APIClients, [key]: newClient },
    }))

    return newClient
  },
  addAPIClient: (key, client) =>
    set((state) => ({
      APIClients: { ...state.APIClients, [key]: client },
    })),
  StudioClients: {},
  getStudioClient: ({ botId, workspaceId }) => {
    const baseURL = `${config.studioLambdaApiUrl}/${botId}`
    const client = get().StudioClients[baseURL]

    if (client) {
      return client
    }
    const cloudPat = config.usePat ? config.pat ?? cookie.get(PAT_KEY) : undefined

    const ax = axios.create({
      baseURL,
      headers: {
        'x-app-id': botId,
        'x-botpress-workspace-id': workspaceId,
        authorization: config.usePat ? `Bearer ${cloudPat}` : undefined,
      },
      withCredentials: !config.usePat,
    })

    set((state) => ({
      StudioClients: { ...state.StudioClients, [baseURL]: ax },
    }))

    return ax
  },
  addStudioClient: (key, client) =>
    set((state) => ({
      StudioClients: { ...state.StudioClients, [key]: client },
    })),
  getHitlUserKey: async (webhookId) => {
    const apiUrl = config.hitlBaseUrl
    const pat = config.pat ?? cookie.get(PAT_KEY)

    return login({
      apiUrl,
      accessToken: pat,
      webhookId,
    }).then(({ key }) => key)
  },
  HITLClients: {},
  addHITLClient: (key, client) =>
    set((state) => ({
      HITLClients: { ...state.HITLClients, [key]: client },
    })),
  getHITLClient: ({ userKey, webhookId }) => {
    const client = get().HITLClients[userKey]

    if (client) {
      return client
    }

    const newClient = createClient({
      apiUrl: config.hitlBaseUrl,
      userKey,
      webhookId,
    })

    set((state) => ({
      HITLClients: { ...state.HITLClients, [userKey]: newClient },
    }))

    return newClient
  },
}))

export const getApiClient = (props?: getApiClientProps) => useClientsStore.getState().getAPIClient(props)
export const getStudioClient = (props: { botId: string; workspaceId: string }) =>
  useClientsStore.getState().getStudioClient(props)

export const getHitlUserKey = (webhookId: string) => useClientsStore.getState().getHitlUserKey(webhookId)
export const getHITLClient = (props: { userKey: string; webhookId: string }) =>
  useClientsStore.getState().getHITLClient(props)

export const getZaiClient = (workspaceId: string, botId: string) => {
  const timeout = 5 * 60 * 1000 // 5 minutes
  const client = new APIClient({
    // We create a new client from scratch here because we need to override the default timeout for Zai
    ...getApiClient({ workspaceId, botId }).config,
    timeout,
  })

  const cognitive = new Cognitive({ client, timeout })

  return new Zai({
    modelId: 'fast',
    client: cognitive,
  })
}
